import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { storage } from '../firebase';

const UserForm = ({ 
  open, 
  onClose, 
  onSubmit, 
  user = null, 
  userType = 'customer',
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dob: null,
    gender: '',
    address: '',
    businessName: '',
    businessType: '',
    experience: '',
    nationality: '',
    preferredGender: '',
    servicesOffered: [],
    abn: '',
    approvalStatus: '',
    // Banking / business
    bankName: '',
    accountName: '',
    bsb: '',
    accountNumber: '',
    abnNumber: '',
    tfnNumber: '',
    // React Native provider fields
    visaCategory: '',
    visaExpiry: null,
    visaExpiryTimestamp: null,
    address_place_id: '',
    formattedAddress: '',
    photoURL: '',
    location: { latitude: '', longitude: '' },
    nationalityStatus: '',
    approvedAt: null,
    rejectedAt: null,
    documents: {}
  });
  
  const [errors, setErrors] = useState({});
  const [documentUploadingKey, setDocumentUploadingKey] = useState('');

  const sectionPaperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    border: '1px solid #dbe3ef',
    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.04)',
    bgcolor: '#ffffff',
  };

  const sectionHeadingSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 2,
  };

  const normalizeDocumentUrl = (value) => {
    if (!value) return '';

    if (typeof value === 'string') {
      return value.trim();
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const normalized = normalizeDocumentUrl(item);
        if (normalized) return normalized;
      }
      return '';
    }

    if (typeof value === 'object') {
      return (
        normalizeDocumentUrl(value.url) ||
        normalizeDocumentUrl(value.downloadURL) ||
        normalizeDocumentUrl(value.fileUrl) ||
        normalizeDocumentUrl(value.uri) ||
        normalizeDocumentUrl(value.path) ||
        ''
      );
    }

    return '';
  };

  const getDocumentValueByKeys = (documents, keys) => {
    if (!documents || typeof documents !== 'object') return '';
    for (const key of keys) {
      const value = normalizeDocumentUrl(documents[key]);
      if (value) return value;
    }
    return '';
  };

  const normalizeGenderValue = (value) => {
    const normalized = String(value || '').trim().toLowerCase();

    if (!normalized) return '';
    if (normalized === 'male') return 'Male';
    if (normalized === 'female') return 'Female';
    if (normalized === 'other') return 'Other';
    if (normalized === 'prefer not to say' || normalized === 'prefer_not_to_say') {
      return 'Prefer not to say';
    }

    return '';
  };

  const normalizePreferredGenderValue = (value) => {
    const normalized = String(value || '').trim().toLowerCase();

    if (!normalized) return '';
    if (normalized === 'any') return 'any';
    if (normalized === 'male' || normalized === 'female') return normalized;
    if (normalized === 'non-binary' || normalized === 'non_binary') return 'non_binary';
    if (normalized === 'prefer not to say' || normalized === 'prefer_not_to_say') {
      return 'prefer_not_to_say';
    }

    return '';
  };

  const documentFieldConfig = [
    {
      key: 'passportUrl',
      label: 'Passport / ID Proof',
      aliases: ['passport', 'idProofUrl', 'idProof'],
    },
    {
      key: 'drivingLicenceUrl',
      label: 'Driving Licence',
      aliases: ['drivingLicenseUrl', 'drivingLicence', 'drivingLicense'],
    },
    {
      key: 'resumeUrl',
      label: 'Resume / CV',
      aliases: ['resume', 'cvUrl', 'cv'],
    },
    {
      key: 'certificatesUrl',
      label: 'Certificates',
      aliases: ['certificates', 'certificateUrl', 'certificate'],
    },
    {
      key: 'verificationVideoUrl',
      label: 'Verification Video',
      aliases: ['verificationVideo'],
    },
    {
      key: 'profilePicture',
      label: 'Profile Picture',
      aliases: ['photoURL', 'profilePhotoUrl'],
    },
  ];

  useEffect(() => {
    // Helper function to safely get string value
    const safeString = (value) => (value && typeof value === 'string') ? value : '';
    
    if (user) {
      // Helper to parse timestamps or ISO strings into Date
      const parseToDate = (val) => {
        if (!val) return null;
        if (val.toDate && typeof val.toDate === 'function') return val.toDate();
        if (val.seconds && typeof val.seconds === 'number') return new Date(val.seconds * 1000);
        // ISO string
        try { return new Date(val); } catch { return null; }
      };

      setFormData({
        name: safeString(user.name),
        email: safeString(user.email),
        phone: safeString(user.phone),
        dob: user.dob ? (user.dob.toDate ? user.dob.toDate() : new Date(user.dob)) : null,
        gender: normalizeGenderValue(user.gender),
        address: safeString(user.address),
        businessName: safeString(user.businessName),
        businessType: safeString(user.businessType),
        experience: safeString(user.experience),
        nationality: safeString(user.nationality),
        preferredGender: normalizePreferredGenderValue(user.preferredGender),
        servicesOffered: Array.isArray(user.servicesOffered) ? user.servicesOffered : (user.servicesOffered ? String(user.servicesOffered).split(',').map(s=>s.trim()) : []),
        abn: safeString(user.abn),
        // banking/business details may be nested under details docs
        bankName: safeString(user.bankName || (user.bankingDetails && user.bankingDetails.bankName)),
        accountName: safeString(user.accountName || (user.bankingDetails && user.bankingDetails.accountName)),
        bsb: safeString(user.bsb || (user.bankingDetails && user.bankingDetails.bsb)),
        accountNumber: safeString(user.accountNumber || (user.bankingDetails && user.bankingDetails.accountNumber)),
        abnNumber: safeString(user.abn || (user.businessInformation && user.businessInformation.abnNumber)),
        tfnNumber: safeString(user.tfn || (user.businessInformation && user.businessInformation.tfnNumber)),
        approvalStatus: safeString(user.approvalStatus),
        // RN fields
        visaCategory: safeString(user.visaCategory),
        visaExpiry: user.visaExpiry ? parseToDate(user.visaExpiry) : (user.visaExpiryTimestamp ? parseToDate(user.visaExpiryTimestamp) : null),
        visaExpiryTimestamp: user.visaExpiryTimestamp || null,
        address_place_id: safeString(user.address_place_id),
        formattedAddress: safeString(user.formattedAddress || user.address),
        photoURL: safeString(user.photoURL),
        location: (user.location && typeof user.location === 'object') ? { latitude: user.location.latitude || user.location.lat || '', longitude: user.location.longitude || user.location.lng || user.location.longitude || '' } : { latitude: '', longitude: '' },
        nationalityStatus: safeString(user.nationalityStatus),
        approvedAt: user.approvedAt ? parseToDate(user.approvedAt) : null,
        rejectedAt: user.rejectedAt ? parseToDate(user.rejectedAt) : null,
        documents: (user.documents && typeof user.documents === 'object') ? user.documents : (user.provider_onboarding && user.provider_onboarding.documents ? user.provider_onboarding.documents : {})
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        dob: null,
        gender: '',
        address: '',
        businessName: '',
        businessType: '',
        experience: '',
        nationality: '',
        approvalStatus: '',
        visaCategory: '',
        visaExpiry: null,
        visaExpiryTimestamp: null,
        address_place_id: '',
        formattedAddress: '',
        photoURL: '',
        location: { latitude: '', longitude: '' },
        nationalityStatus: '',
        approvedAt: null,
        rejectedAt: null,
        documents: {}
      });
    }
    setErrors({});
    setDocumentUploadingKey('');
  }, [user, userType, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleDocumentFileChange = async (row, file) => {
    if (!file) return;

    const providerId = user?.id || formData.id || user?.uid || formData.uid;
    if (!providerId) {
      setErrors(prev => ({
        ...prev,
        documents: 'Save the provider first before uploading documents.',
      }));
      return;
    }

    setDocumentUploadingKey(row.key);
    setErrors(prev => ({ ...prev, documents: null }));

    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const safeKey = row.key.replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileRef = ref(
        storage,
        `provider_documents/${providerId}/dashboard_replacements/${safeKey}_${Date.now()}_${safeName}`,
      );

      await uploadBytes(fileRef, file, { contentType: file.type || undefined });
      const downloadUrl = await getDownloadURL(fileRef);
      const docs =
        formData.documents && typeof formData.documents === 'object'
          ? formData.documents
          : {};

      handleChange('documents', {
        ...docs,
        [row.key]: downloadUrl,
      });

      if (row.key === 'profilePicture') {
        handleChange('photoURL', downloadUrl);
      }
    } catch (error) {
      console.error('Failed to replace provider document:', error);
      setErrors(prev => ({
        ...prev,
        documents: error?.message || 'Unable to upload this document.',
      }));
    } finally {
      setDocumentUploadingKey('');
    }
  };





  const validateForm = () => {
    const newErrors = {};

    // Helper function to safely check string fields
    const isValidString = (value) => {
      return value && typeof value === 'string' && value.trim().length > 0;
    };

    // Basic field validation with null/undefined checks
    if (!isValidString(formData.name)) {
      newErrors.name = 'Name is required';
    }
    
    if (!isValidString(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!isValidString(formData.phone)) {
      newErrors.phone = 'Phone is required';
    }
    
    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }
    
    if (!isValidString(formData.gender)) {
      newErrors.gender = 'Gender is required';
    }

    // Address validation - handle both string and object formats
    if (formData.address) {
      if (typeof formData.address === 'string') {
        // If address is a string, just check if it's not empty
        if (!isValidString(formData.address)) {
          newErrors.address = 'Address is required';
        }
      } else if (typeof formData.address === 'object' && formData.address !== null) {
        // If address is an object, validate individual fields
        if (!isValidString(formData.address.street)) {
          newErrors.street = 'Street address is required';
        }
        if (!isValidString(formData.address.city)) {
          newErrors.city = 'City is required';
        }
        if (!isValidString(formData.address.state)) {
          newErrors.state = 'State is required';
        }
        if (!isValidString(formData.address.zipCode)) {
          newErrors.zipCode = 'Zip code is required';
        }
      }
    } else {
      newErrors.address = 'Address is required';
    }

    // Client-specific validation
    if (userType === 'client') {
      if (!isValidString(formData.businessName)) {
        newErrors.businessName = 'Business name is required';
      }
      if (!isValidString(formData.businessType)) {
        newErrors.businessType = 'Business type is required';
      }
      if (!isValidString(formData.abn)) {
        newErrors.abn = 'ABN is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (documentUploadingKey) {
      setErrors(prev => ({
        ...prev,
        documents: 'Please wait for the document upload to finish.',
      }));
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      dob: null,
      gender: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Australia'
      },
      location: { latitude: '', longitude: '' },
      roles: {
        customer: userType === 'customer',
        client: userType === 'client',
        admin: false
      },
      businessName: '',
      businessType: '',
      abn: '',
      bankName: '',
      accountName: '',
      bsb: '',
      accountNumber: '',
      abnNumber: '',
      tfnNumber: '',
      preferredGender: '',
      servicesOffered: [],
      businessAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Australia'
      },
      contactPerson: '',
      website: ''
      ,
      documents: {}
    });
    setErrors({});
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            height: '92vh',
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #f8fafc 0%, #eef3fa 100%)',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 2.5,
            color: '#ffffff',
            background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <IconButton onClick={handleClose} size="small" sx={{ color: '#ffffff' }}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {user ? `Edit ${userType}` : `Add New ${userType}`}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              Review profile details and update provider documents in one place
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent
          dividers
          sx={{
            backgroundColor: 'transparent',
            p: { xs: 2, sm: 3 },
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <Paper
              sx={{
                ...sectionPaperSx,
                mb: 2,
                background:
                  'linear-gradient(135deg, rgba(29, 78, 216, 0.10) 0%, rgba(15, 23, 42, 0.06) 100%)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  {formData.photoURL ? (
                    <Box
                      component="img"
                      src={formData.photoURL}
                      alt={formData.name || 'Provider'}
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid rgba(29, 78, 216, 0.4)',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#dbeafe',
                        color: '#1d4ed8',
                      }}
                    >
                      <PersonIcon />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#334155' }}>
                      Provider Edit Workspace
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {formData.name || 'Unnamed Provider'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {formData.email || 'No email yet'}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={
                    formData.approvalStatus
                      ? `Status: ${formData.approvalStatus}`
                      : user
                      ? 'Status: Existing'
                      : 'Status: Draft'
                  }
                  size="small"
                  sx={{
                    fontWeight: 600,
                    color: '#1e3a8a',
                    bgcolor: 'rgba(219, 234, 254, 0.9)',
                  }}
                />
              </Box>
            </Paper>

            <Paper sx={{ ...sectionPaperSx, mb: 2 }}>
              <Box sx={sectionHeadingSx}>
                <PersonIcon fontSize="small" sx={{ color: '#1d4ed8' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Personal Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Date of Birth"
                    value={formData.dob ?? null}
                    onChange={(date) => handleChange('dob', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.dob,
                        helperText: errors.dob,
                        required: true,
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth error={!!errors.gender} required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.gender ?? ''}
                      label="Gender"
                      onChange={(e) => handleChange('gender', e.target.value)}
                    >
                      <MenuItem value="">Select gender</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                      <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ ...sectionPaperSx, mb: 2 }}>
              <Box sx={sectionHeadingSx}>
                <HomeIcon fontSize="small" sx={{ color: '#1d4ed8' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Address
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ ...sectionPaperSx, mb: 2 }}>
              <Box sx={sectionHeadingSx}>
                <BusinessCenterIcon fontSize="small" sx={{ color: '#1d4ed8' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Provider Details
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Business Name"
                    value={formData.businessName}
                    onChange={(e) => handleChange('businessName', e.target.value)}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Business Type"
                    value={formData.businessType}
                    onChange={(e) => handleChange('businessType', e.target.value)}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Experience"
                    value={formData.experience}
                    onChange={(e) => handleChange('experience', e.target.value)}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="ABN"
                    value={formData.abn}
                    onChange={(e) => handleChange('abn', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="TFN"
                    value={formData.tfnNumber}
                    onChange={(e) => handleChange('tfnNumber', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    value={formData.bankName}
                    onChange={(e) => handleChange('bankName', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    value={formData.accountName}
                    onChange={(e) => handleChange('accountName', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="BSB"
                    value={formData.bsb}
                    onChange={(e) => handleChange('bsb', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    value={formData.accountNumber}
                    onChange={(e) => handleChange('accountNumber', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Preferred Gender</InputLabel>
                    <Select
                      value={formData.preferredGender ?? ''}
                      label="Preferred Gender"
                      onChange={(e) => handleChange('preferredGender', e.target.value)}
                    >
                      <MenuItem value="">Select preference</MenuItem>
                      <MenuItem value="any">Any</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="non_binary">Non-binary</MenuItem>
                      <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Services Offered (comma separated)"
                    value={Array.isArray(formData.servicesOffered) ? formData.servicesOffered.join(', ') : formData.servicesOffered}
                    onChange={(e) => handleChange('servicesOffered', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Visa Category"
                    value={formData.visaCategory}
                    onChange={(e) => handleChange('visaCategory', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Visa Expiry"
                    value={formData.visaExpiry ?? null}
                    onChange={(date) => handleChange('visaExpiry', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Formatted Address"
                    value={formData.formattedAddress}
                    onChange={(e) => handleChange('formattedAddress', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Address Place ID"
                    value={formData.address_place_id}
                    onChange={(e) => handleChange('address_place_id', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Photo URL"
                    value={formData.photoURL}
                    onChange={(e) => handleChange('photoURL', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Location Latitude"
                    value={formData.location?.latitude || ''}
                    onChange={(e) =>
                      handleChange('location', {
                        ...(formData.location || { latitude: '', longitude: '' }),
                        latitude: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Location Longitude"
                    value={formData.location?.longitude || ''}
                    onChange={(e) =>
                      handleChange('location', {
                        ...(formData.location || { latitude: '', longitude: '' }),
                        longitude: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nationality"
                    value={formData.nationality}
                    onChange={(e) => handleChange('nationality', e.target.value)}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Approval Status"
                    value={formData.approvalStatus}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nationality Status"
                    value={formData.nationalityStatus}
                    onChange={(e) => handleChange('nationalityStatus', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Approved At"
                    value={formData.approvedAt ? formData.approvedAt.toISOString() : ''}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Rejected At"
                    value={formData.rejectedAt ? formData.rejectedAt.toISOString() : ''}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={sectionPaperSx}>
              <Box sx={sectionHeadingSx}>
                <DescriptionIcon fontSize="small" sx={{ color: '#1d4ed8' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Documents
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                View uploaded files and replace provider documents without exposing storage URLs.
              </Typography>
              {errors.documents ? (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {errors.documents}
                </Typography>
              ) : null}
              <Grid container spacing={2}>
                {(() => {
                  const docs =
                    formData.documents && typeof formData.documents === 'object'
                      ? formData.documents
                      : {};
                  const technicalDocumentKeys = new Set([
                    'secureDocuments',
                    'secureDocumentIds',
                    'secureStoragePaths',
                    'secureUploadSessionId',
                    'secureUploadStatus',
                  ]);

                  const knownKeys = new Set();
                  const mappedRows = documentFieldConfig.map((field) => {
                    const lookupKeys = [field.key, ...(field.aliases || [])];
                    lookupKeys.forEach((key) => knownKeys.add(key));
                    const value =
                      field.key === 'profilePicture'
                        ? getDocumentValueByKeys(docs, lookupKeys) ||
                          normalizeDocumentUrl(formData.photoURL) ||
                          normalizeDocumentUrl(user?.photoURL)
                        : getDocumentValueByKeys(docs, lookupKeys);
                    return {
                      key: field.key,
                      label: field.label,
                      value,
                    };
                  });

                  const extraRows = Object.keys(docs)
                    .filter((key) => !knownKeys.has(key) && !technicalDocumentKeys.has(key))
                    .sort()
                    .map((key) => ({
                      key,
                      label: key,
                      value: getDocumentValueByKeys(docs, [key]),
                    }));

                  const rows = [...mappedRows, ...extraRows];

                  if (rows.length === 0) {
                    return (
                      <Grid size={{ xs: 12 }}>
                        <Typography color="text.secondary">
                          No documents found for this provider.
                        </Typography>
                      </Grid>
                    );
                  }

                  return rows.map((row) => (
                    <Grid size={{ xs: 12, md: 6, lg: 4 }} key={row.key}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                          minWidth: 0,
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                          <DescriptionIcon sx={{ color: row.value ? '#1d4ed8' : '#94a3b8', mt: 0.25 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontWeight: 700 }} noWrap>
                              {row.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              color={row.value ? 'text.secondary' : 'text.disabled'}
                              sx={{ fontStyle: row.value ? 'normal' : 'italic' }}
                            >
                              {row.value ? 'Document uploaded' : 'No document uploaded yet'}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={row.value ? 'success' : 'default'}
                            label={row.value ? 'Uploaded' : 'Empty'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 'auto' }}>
                          {row.value ? (
                            <Button
                              size="small"
                              variant="outlined"
                              href={row.value}
                              target="_blank"
                              rel="noreferrer"
                              startIcon={<OpenInNewIcon fontSize="small" />}
                            >
                              Open
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            variant={row.value ? 'outlined' : 'contained'}
                            component="label"
                            disabled={loading || Boolean(documentUploadingKey)}
                            startIcon={<CloudUploadIcon fontSize="small" />}
                          >
                            {documentUploadingKey === row.key
                              ? 'Uploading...'
                              : row.value
                                ? 'Replace'
                                : 'Upload'}
                            <input
                              hidden
                              type="file"
                              accept={
                                row.key === 'verificationVideoUrl'
                                  ? 'video/*'
                                  : 'image/*,application/pdf'
                              }
                              onChange={(event) => {
                                handleDocumentFileChange(row, event.target.files?.[0]);
                                event.target.value = '';
                              }}
                            />
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  ));
                })()}
              </Grid>
            </Paper>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || Boolean(documentUploadingKey)}
          >
            {loading ? 'Saving...' : (user ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default UserForm;
